/**
 * Récupère TOUTES les tâches d'un projet en bouclant sur la pagination serveur, au lieu de se
 * contenter d'une seule page.
 *
 * Le serveur plafonne `pageSize` à `LIMITS.maxPageSize` (200, décision assumée dans
 * `shared-types`) : une vue qui demandait `pageSize=500` (Gantt, Planning) ou `pageSize=200`
 * (Kanban) voyait sa requête silencieusement ramenée à 200 et perdait tout ce qui dépassait —
 * sans le moindre indicateur. Le Gantt calculait alors un chemin critique sur un graphe amputé
 * (issue B5) : les dépendances des tâches manquantes pendaient dans le vide.
 *
 * La boucle et sa borne de sécurité (`MAX_PAGES`, `truncated: true` si atteinte) vivent dans
 * `lib/pagination.js`, partagées avec toutes les autres listes du client.
 */

import { useCallback } from 'react';
import { fetchAllPages } from '../lib/pagination.js';
import { useAsync } from './useAsync.js';

/**
 * @param {string} projectId
 * @param {{ query?: string, deps?: readonly unknown[] }} [options]
 *   `query` : paramètres additionnels déjà encodés (ex. `status=done`), sans `page`/`pageSize`.
 *   `deps` : dépendances supplémentaires qui doivent déclencher un rechargement (ex. `reloadKey`).
 */
export function useAllTasks(projectId, { query = '', deps = [] } = {}) {
  const loader = useCallback(
    async (signal) => {
      const { data, truncated } = await fetchAllPages(
        `/projects/${projectId}/tasks${query ? `?${query}` : ''}`,
        { signal },
      );
      return { tasks: data, truncated };
    },
    [projectId, query],
  );

  const { data, loading, error, reload, patch } = useAsync(loader, [projectId, query, ...deps]);

  /** Met à jour la liste des tâches sans toucher au drapeau `truncated`. */
  const patchTasks = useCallback(
    (updater) =>
      patch((current) => ({
        tasks: updater(current?.tasks ?? []),
        truncated: current?.truncated ?? false,
      })),
    [patch],
  );

  return {
    tasks: data?.tasks ?? null,
    truncated: data?.truncated ?? false,
    loading,
    error,
    reload,
    patch: patchTasks,
  };
}
